import type { WebContainerProcess } from "./types";

const OSC = (msg: string) => `\x1b]654;${msg}\x07`;
const ETX = "\x03"; // Ctrl+C (end-of-text)

// Shell metacharacters that require a real shell to interpret. If a segment
// contains any of these, we can't spawn it as a plain argv and must defer to
// `sh -c`. Covers pipes, redirects, sequencing, subshells, background, globs,
// brace expansion, variable/command substitution, and quoting. A bare `=`
// is deliberately allowed so flag args like `--port=5173` still spawn direct;
// only a genuine `VAR=val cmd` env-prefix would misbehave (rare, and never on
// the dev-server command that must not route through the hanging `sh -c`).
// NOTE: `&&` chaining and `#` comments are handled BEFORE this check, so a
// segment reaching here has had those stripped/split out already.
const SHELL_META = /[|&;<>()$`\\"'*?{}\[\]]/;

// Strip a trailing POSIX comment: an unquoted `#` at the start of a word
// (index 0 or preceded by whitespace) begins a comment through end of line.
// dualite appends ` #ai` to AI commands as a marker — in a real shell that is
// a comment; jsh must treat it the same or it becomes a stray argv token that
// breaks e.g. `yarn run dev #ai` (→ vite receives `#ai` as a positional).
export function stripComment(line: string): string {
  let inSingle = false;
  let inDouble = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (inSingle) { if (c === "'") inSingle = false; continue; }
    if (inDouble) { if (c === '"') inDouble = false; continue; }
    if (c === "'") { inSingle = true; continue; }
    if (c === '"') { inDouble = true; continue; }
    if (c === "#" && (i === 0 || /\s/.test(line[i - 1]))) {
      return line.slice(0, i);
    }
  }
  return line;
}

// Split a command line on top-level `&&` (quote-aware), returning the segments.
// A single-segment line yields a one-element array. `&&` inside quotes is left
// intact. This lets us spawn each segment directly and run them sequentially
// with short-circuit-on-failure semantics — crucial because a `&&`-joined line
// ending in a long-running dev server (e.g. `yarn install && yarn run dev`,
// what dualite's "Run Preview" sends) must NOT go through `sh -c`, whose
// builtin cannot host a server (it never binds; server-ready never fires).
export function splitOnAnd(line: string): string[] {
  const segments: string[] = [];
  let inSingle = false;
  let inDouble = false;
  let start = 0;
  for (let i = 0; i < line.length - 1; i++) {
    const c = line[i];
    if (inSingle) { if (c === "'") inSingle = false; continue; }
    if (inDouble) { if (c === '"') inDouble = false; continue; }
    if (c === "'") { inSingle = true; continue; }
    if (c === '"') { inDouble = true; continue; }
    if (c === "&" && line[i + 1] === "&") {
      segments.push(line.slice(start, i));
      i++; // skip the second '&'
      start = i + 1;
    }
  }
  segments.push(line.slice(start));
  return segments;
}

// Split an operator-free segment into argv on whitespace. Returns null if the
// segment contains shell syntax (so the caller falls back to `sh -c`), or if it
// is empty. Intentionally conservative: any quoting/expansion → null.
function tokenizeSimpleCommand(command: string): string[] | null {
  if (SHELL_META.test(command)) return null;
  const argv = command.split(/\s+/).filter(Boolean);
  return argv.length ? argv : null;
}

interface NodepodProcessLike {
  on(event: "output" | "error" | "exit", handler: (arg: any) => void): unknown;
  completion: Promise<{ exitCode: number }>;
  kill(): void;
}
interface NodepodLike {
  cwd: string;
  spawn(cmd: string, args?: string[], opts?: { cwd?: string }): Promise<NodepodProcessLike>;
}

// Visible shell prompt, shown only in interactive (echo) mode so a real
// terminal has something to type against. Programmatic callers never see it.
const PROMPT = "$ ";

export function createJshProcess(
  nodepod: NodepodLike,
  opts?: { cwd?: string; echo?: boolean },
): WebContainerProcess {
  const cwd = opts?.cwd ?? nodepod.cwd;
  // Echo mode = cooked-mode line discipline for an interactive xterm: echo
  // typed characters, treat CR as Enter, support Backspace, show a prompt.
  // Off (default) = the byte-for-byte programmatic path used by the AI shell.
  const echo = opts?.echo ?? false;
  let emit: (chunk: string) => void = () => {};
  let closed = false;
  let killed = false;
  let currentChild: NodepodProcessLike | null = null;

  const output = new ReadableStream<string>({
    start(controller) {
      emit = (chunk) => { if (!closed) controller.enqueue(chunk); };
      // become interactive on next microtask so listeners attach first
      queueMicrotask(() => {
        emit(OSC("interactive"));
        if (echo) emit(PROMPT);
      });
    },
  });

  let running: Promise<void> = Promise.resolve();
  let lineBuffer = "";
  let lastWasCR = false; // for CRLF collapse in echo mode
  let pidSeq = 1000; // synthetic pid for the exit OSC (see emitExit)

  // Emit the exit OSC in jsh's real `exit=<pid>:<code>` shape. The consumer
  // parses the exit code from the SECOND number; the pid (first) is unused but
  // MUST be present — a bare `exit=<code>` fails the consumer's regex, so it
  // never detects completion and its command-wait hangs forever.
  function emitExit(exitCode: number) {
    emit(OSC(`exit=${++pidSeq}:${exitCode}`));
  }

  // Emit the end-of-command prompt: OSC markers dualite parses, plus a visible
  // prompt line in echo mode.
  function emitPrompt() {
    emit(OSC("prompt"));
    emit(OSC("interactive"));
    if (echo) emit("\r\n" + PROMPT);
  }

  function queueLine(line: string) {
    running = running.then(() => runCommand(line));
  }

  // Ctrl+C: interrupt the foreground process (e.g. a running dev server) and
  // return to a fresh prompt. Killing an in-flight child lets its runSegment
  // completion resolve → the queued command chain advances and emits the
  // prompt; if nothing is running, emit the prompt directly. Discards any
  // partial line either way.
  function handleInterrupt() {
    lineBuffer = "";
    if (echo) emit("^C");
    if (currentChild) {
      currentChild.kill();
    } else {
      emitPrompt();
    }
  }

  // Spawn one segment (a single command, no top-level `&&`). Operator-free
  // segments spawn argv directly; anything with shell syntax defers to `sh -c`.
  // Tracks currentChild so a Ctrl+C can kill an in-flight (possibly long-running)
  // process and let the queue advance. Returns the exit code (1 on spawn error).
  async function runSegment(segment: string): Promise<number> {
    const argv = tokenizeSimpleCommand(segment);
    try {
      const child = argv
        ? await nodepod.spawn(argv[0], argv.slice(1), { cwd })
        : await nodepod.spawn("sh", ["-c", segment], { cwd });
      currentChild = child;
      child.on("output", (c: string) => emit(c));
      child.on("error", (c: string) => emit(c));
      const { exitCode } = await child.completion;
      currentChild = null;
      return exitCode;
    } catch {
      currentChild = null;
      return 1;
    }
  }

  async function runCommand(rawLine: string) {
    if (killed) return;
    const line = stripComment(rawLine).trim();
    if (!line) { emitPrompt(); return; }

    // `&&` chain: run each segment in order, stop on the first non-zero exit.
    let exitCode = 0;
    for (const seg of splitOnAnd(line)) {
      const s = seg.trim();
      if (!s) continue;
      exitCode = await runSegment(s);
      if (killed) return;
      if (exitCode !== 0) break; // && short-circuit
    }
    emitExit(exitCode);
    emitPrompt();
  }

  // Programmatic input: whole command lines terminated by `\n` (the AI shell
  // and Run Preview). No echo. Ctrl+C interrupts. Behavior is unchanged from
  // before echo mode existed.
  function writeProgrammatic(chunk: string) {
    const etx = chunk.lastIndexOf(ETX);
    if (etx !== -1) {
      chunk = chunk.slice(etx + 1);
      handleInterrupt();
    }
    lineBuffer += chunk;
    let nl: number;
    while ((nl = lineBuffer.indexOf("\n")) !== -1) {
      const line = lineBuffer.slice(0, nl);
      lineBuffer = lineBuffer.slice(nl + 1);
      queueLine(line);
    }
  }

  // Interactive input: cooked-mode line editing for an xterm. Echo printable
  // characters, run on CR/LF (CRLF collapses to one Enter), erase on Backspace,
  // interrupt on Ctrl+C. Processes byte-by-byte so pastes (multi-char chunks)
  // work naturally.
  function writeInteractive(chunk: string) {
    for (const ch of chunk) {
      if (ch === "\n" && lastWasCR) { lastWasCR = false; continue; } // CRLF → one Enter
      lastWasCR = ch === "\r";

      if (ch === ETX) {
        handleInterrupt();
      } else if (ch === "\r" || ch === "\n") {
        emit("\r\n");
        const line = lineBuffer;
        lineBuffer = "";
        queueLine(line);
      } else if (ch === "\x7f" || ch === "\b") {
        if (lineBuffer.length > 0) {
          lineBuffer = lineBuffer.slice(0, -1);
          emit("\b \b"); // move back, overwrite with space, move back
        }
      } else if (ch >= " " || ch === "\t") {
        lineBuffer += ch;
        emit(ch);
      }
      // other control characters are ignored
    }
  }

  const input = new WritableStream<string>({
    write(chunk) {
      if (echo) writeInteractive(chunk);
      else writeProgrammatic(chunk);
    },
  });

  return {
    output,
    input,
    exit: new Promise<number>(() => {}), // interactive shell never exits on its own
    kill: () => { closed = true; killed = true; currentChild?.kill(); },
    resize: () => {},
  };
}
